import { isNumber, isString } from 'lodash-es';
import { forwardRef, useCallback, useRef } from 'react';

import type { ClickableProps } from '#@/components/Clickable';
import type { As } from '#@/shared/constants/styledComponents';
import { COLOR } from '#@/shared/utils/styles';

import Badge from '../Badge';
import Checkbox, { type CheckboxProps } from '../Checkbox';
import HoverableText from '../HoverableText';
import Icon, { type IconProps } from '../Icon';
import RadioButton, { type RadioButtonProps } from '../RadioButton';
import {
  CheckboxWrapper,
  DropdownActionWrapper,
  IconRightWrapper,
  IconWrapper,
  Indentation,
  RadioButtonWrapper,
  SubtitleBottom,
  SubtitleRight,
  TextWrapper,
} from './Styles';

export interface IconConfiguration {
  type: IconProps['type'];
  color?: IconProps['color'];
}

export interface DropdownActionProps extends ClickableProps {
  isDisplayed: boolean;
  as?: As;
  iconLeft?: IconConfiguration;
  iconRight?: IconConfiguration;
  subtitleBottom?: string;
  subtitleRight?: React.ReactNode;
  badgeCount?: number;
  checkboxProps?: Omit<CheckboxProps, 'checkboxLabel'>;
  radioButtonProps?: Omit<RadioButtonProps, 'label'>;
  selected?: boolean;
  hovered?: boolean;
  indented?: boolean;
}

const DropdownAction = forwardRef<HTMLButtonElement, DropdownActionProps>(
  (
    {
      onClick,
      isDisplayed,
      iconLeft,
      iconRight,
      subtitleBottom,
      subtitleRight,
      badgeCount,
      checkboxProps,
      radioButtonProps,
      selected,
      hovered,
      indented,
      children,
      ...props
    },
    ref,
  ) => {
    const checkboxRef = useRef<HTMLInputElement>(null);
    const radioButtonRef = useRef<HTMLInputElement>(null);

    const visibilityProps = isDisplayed ? {} : { tabIndex: -1, 'aria-hidden': true };

    const handleClick: ClickableProps['onClick'] = (...args) => {
      if (checkboxProps && checkboxRef.current) {
        checkboxRef.current.click();
      }
      if (radioButtonProps && radioButtonRef.current) {
        radioButtonRef.current.click();
      }
      if (onClick) {
        onClick(...args);
      }
    };

    const renderSubtitleRight = useCallback(() => {
      if (!subtitleRight) {
        return null;
      }
      if (isString(subtitleRight) || isNumber(subtitleRight)) {
        return <SubtitleRight $selected={selected}>{subtitleRight}</SubtitleRight>;
      }
      return subtitleRight;
    }, [subtitleRight, selected]);

    return (
      <DropdownActionWrapper
        ref={ref}
        onClick={handleClick}
        $selected={selected}
        $hovered={hovered}
        {...visibilityProps}
        {...props}
      >
        {checkboxProps && (
          <CheckboxWrapper>
            <Checkbox ref={checkboxRef} {...checkboxProps} />
          </CheckboxWrapper>
        )}
        {radioButtonProps && (
          <RadioButtonWrapper>
            <RadioButton ref={radioButtonRef} {...radioButtonProps} />
          </RadioButtonWrapper>
        )}
        {iconLeft && (
          <IconWrapper>
            <Icon type={iconLeft.type} color={selected ? COLOR.white : iconLeft.color} size={18} />
          </IconWrapper>
        )}
        {indented && <Indentation />}
        <TextWrapper>
          <HoverableText text={children} />
          {subtitleBottom && <SubtitleBottom $selected={selected}>{subtitleBottom}</SubtitleBottom>}
        </TextWrapper>
        {renderSubtitleRight()}
        {badgeCount !== undefined && <Badge count={badgeCount} color={COLOR.brand} />}
        {iconRight && (
          <IconRightWrapper>
            <Icon type={iconRight.type} color={selected ? COLOR.white : iconRight.color} />
          </IconRightWrapper>
        )}
      </DropdownActionWrapper>
    );
  },
);

DropdownAction.displayName = 'DropdownAction';

export default DropdownAction;
export { DropdownActionSeparator, DropdownSectionHeading } from './Styles';
